import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';

/**
 * This hook is used to manage a search parameter in the URL.
 * It will update the search parameter in the URL when the value changes.
 * It will also load the initial value from the URL if no value is provided.
 *
 * @example
 * const [clusterIds, setClusterIds] = useStateSearchParam({
 *   parameter: 'clusterIds',
 * });
 *
 */
export function useStateSearchParam<ValueType>({
  parameter,
  value: _value,
}: {
  parameter: string;
  value?: ValueType;
}): [ValueType | undefined, Dispatch<SetStateAction<ValueType | undefined>>] {
  const location = useLocation();
  const navigate = useNavigate();
  const [value, setValue] = useState<ValueType | undefined>(
    _value ??
      ((location.search as Record<string, unknown>)[parameter] as ValueType)
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigate({
      to: location.pathname,
      search: {
        ...location.search,
        ...(value && { [parameter]: value }),
      },
    });
  }, [location.pathname, location.search, navigate, parameter, value]);

  return [value, setValue];
}
